import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCatalogText } from '@/lib/localizeCatalog';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { closeDrawer, selectCartMode } from '@/features/cart/cartSlice';
import { selectIsInDineIn, selectTableSession } from '@/features/tableSession/tableSessionSlice';
import { isWaiterTableSession } from '@/features/waiter/waiterSurface';
import Modal, { type ModalOrigin } from '@/components/ui/Modal';
import QtyStepper from '@/components/ui/QtyStepper';
import Price from '@/components/ui/Price';
import { computeUnitPrice } from './computeUnitPrice';
import { minRequired, isEffectivelyRequired } from './optionRequirement';
import type { CatalogTranslations, MenuItem, OptionChoice, OptionGroup, SelectedOption } from '@/types';
import { useFlyToCart } from '@/components/fx';
import SteamLayer from './SteamLayer';
import { quickAddToCart, type SharedAddLineArgs } from './quickAddToCart';
import ProductImage from './ProductImage';
import DishCodeTag from './DishCodeTag';
import AllergenSection from './allergens/AllergenSection';
import type { ProductAllergens } from './allergens/allergens';

interface Props {
  open: boolean;
  onClose: () => void;
  item: MenuItem | null;
  groups: OptionGroup[];
  restaurantUuid: string;
  origin?: ModalOrigin | null;
  orderingEnabled?: boolean;
  restaurantLogoUrl: string | null;
  restaurantName: string;
  themeColor: string;
  /** Advisory buffet budget exceeded flag. When true, the add-to-cart button
   *  is disabled as a UX hint. Server is always authoritative. */
  budgetExceeded?: boolean;
  /** SC-169: when present, the table is in shared-cart mode — the add routes to
   *  the server-side shared cart via this callback instead of the local cart. */
  addSharedLine?: (args: SharedAddLineArgs) => void;
  /** SC-218: buffet dine-in session — an "included" product prices its base at 0. */
  isBuffetDineIn?: boolean;
  /** SC-1280 — déclaration du plat ; `undefined` = non renseigné, `contains: []` = sans allergène majeur, `traces` = peut contenir des traces. */
  allergens?: ProductAllergens;
  /** SC-1280 — le restaurant a déclaré au moins un produit (sinon aucune section allergènes). */
  allergensDeclared?: boolean;
}

export default function ProductModal({
  open,
  onClose,
  item,
  groups,
  restaurantUuid,
  origin,
  orderingEnabled = true,
  restaurantLogoUrl,
  restaurantName,
  themeColor,
  budgetExceeded = false,
  addSharedLine,
  isBuffetDineIn = false,
  allergens,
  allergensDeclared = false,
}: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const flyToCart = useFlyToCart();
  const [qty, setQty] = useState(1);
  const [selected, setSelected] = useState<SelectedOption[]>([]);
  const [comment, setComment] = useState('');
  const [step, setStep] = useState(0);

  // SC-250: resolve the active fulfillment mode the same way DishCard does, so
  // the modal's "Ajouter au panier" amount and the stored cart line use the
  // per-mode price (e.g. sur_place), not the mode-agnostic default.
  const isInDineIn = useAppSelector(selectIsInDineIn);
  const tableSession = useAppSelector(selectTableSession);
  // Le commentaire libre est reserve au carnet du serveur. Ouvert au client, il
  // devient une porte derobee : « + porc », « double viande » s'impriment sur
  // le bon exactement comme une option payante, la cuisine sert le supplement
  // et le comptoir n'en sait rien. Le serveur (sale_api) refait ce tri : le
  // masquage ici n'est que l'UX du meme choix.
  // SC-692: la presence d'un jeton serveur sur l'appareil ne suffit pas — il
  // survit dans localStorage et le client qui scanne le QR sur ce meme telephone
  // heritait du champ. Ce qui compte est la table servie : ouverte depuis
  // /serveur (carnet du serveur) ou scannee par le client.
  const isWaiter = isWaiterTableSession(tableSession, restaurantUuid);
  // SC-1278 — le serveur garde les noms français (ce qui sort sur le bon de cuisine).
  const ct = useCatalogText();
  const nameOf = (e: { name?: string; label?: string; translations?: CatalogTranslations }) =>
    isWaiter ? (e.name ?? e.label ?? '') : ct.name(e);
  const itemName = item ? nameOf(item) : '';
  const itemDescription = item ? (isWaiter ? item.description : ct.description(item)) : '';
  const cartMode = useAppSelector(selectCartMode(restaurantUuid));
  const activeMode = isInDineIn ? 'dine_in' : cartMode;

  // Per-product resolved groups (per-product price + is_active applied). Fall
  // back to the shared list only for callers that don't populate it. (SC-93)
  const itemGroups: OptionGroup[] =
    item?.optionGroups ??
    (item?.optionGroupIds ?? [])
      .map((gid) => groups.find((g) => g.id === gid))
      .filter((g): g is OptionGroup => g !== undefined);

  // reset when item changes
  useMemo(() => {
    setQty(1);
    setStep(0);
    setComment('');
    setSelected(
      itemGroups
        .filter((g) => g.required && !g.multiple && g.options.length > 0)
        .map((g) => ({ groupId: g.id, options: [{ id: g.options[0].id, qty: 1 }] }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  if (!item) return null;

  const unitPrice = computeUnitPrice(item, itemGroups, selected, isBuffetDineIn, activeMode);

  // SC-237: a buffet-included product in a buffet dine-in session has all options
  // free — hide the per-option supplement labels (the unit total is already 0).
  const buffetIncluded = isBuffetDineIn && !!item.includedInBuffet;

  const optionQty = (groupId: string, optionId: string): number =>
    selected.find((s) => s.groupId === groupId)?.options.find((o) => o.id === optionId)?.qty ?? 0;

  const isOptionSelected = (groupId: string, optionId: string) => optionQty(groupId, optionId) > 0;

  const maxAllowed = (g: OptionGroup) =>
    g.max && g.max > 0 ? g.max : g.multiple ? Number.POSITIVE_INFINITY : 1;
  const selectedCount = (groupId: string) => {
    const entry = selected.find((s) => s.groupId === groupId);
    return entry ? entry.options.reduce((sum, o) => sum + o.qty, 0) : 0;
  };

  // Per-option quantity bounds. min is conditional — enforced only once the
  // option is selected (selecting jumps straight to min); max null = no
  // per-option cap, the group budget is the only bound.
  const optionMinQty = (opt: OptionChoice) => Math.max(1, opt.minQty ?? 0);
  const optionMaxQty = (opt: OptionChoice) =>
    opt.maxQty != null ? opt.maxQty : Number.POSITIVE_INFINITY;

  const setOptionQty = (group: OptionGroup, optionId: string, nextQty: number) => {
    setSelected((prev) => {
      const existing = prev.find((s) => s.groupId === group.id);
      const safeQty = Math.max(0, Math.floor(nextQty));
      if (!existing) {
        if (safeQty === 0) return prev;
        return [...prev, { groupId: group.id, options: [{ id: optionId, qty: safeQty }] }];
      }
      const nextOptions = existing.options.filter((o) => o.id !== optionId);
      if (safeQty > 0) nextOptions.push({ id: optionId, qty: safeQty });
      if (nextOptions.length === 0) return prev.filter((s) => s.groupId !== group.id);
      return prev.map((s) => (s.groupId === group.id ? { ...s, options: nextOptions } : s));
    });
  };

  const toggleSingleChoice = (group: OptionGroup, optionId: string) => {
    setSelected((prev) => {
      const existing = prev.find((s) => s.groupId === group.id);
      const isSel = existing?.options.some((o) => o.id === optionId);
      if (isSel && !group.required) {
        return prev.filter((s) => s.groupId !== group.id);
      }
      if (existing) {
        return prev.map((s) =>
          s.groupId === group.id ? { ...s, options: [{ id: optionId, qty: 1 }] } : s
        );
      }
      return [...prev, { groupId: group.id, options: [{ id: optionId, qty: 1 }] }];
    });
  };

  const allRequiredFilled = itemGroups.every((g) => selectedCount(g.id) >= minRequired(g));
  // Ecran unique du serveur : le bouton « Ajouter » y est desactive tant qu'un
  // groupe obligatoire est vide, sans etape pour le lui apprendre. On nomme donc
  // ce qui manque, sinon le bouton semble simplement casse.
  const missingRequiredLabels = itemGroups
    .filter((g) => selectedCount(g.id) < minRequired(g))
    .map((g) => nameOf(g));
  // Advisory buffet gating: disable add when the server-reported round budget is exceeded.
  const addDisabledByBudget = budgetExceeded;

  const onAdd = (e: React.MouseEvent<HTMLButtonElement>) => {
    quickAddToCart({
      dispatch,
      flyToCart,
      restaurantUuid,
      item,
      itemGroups,
      sourceEl: e.currentTarget,
      qty,
      selected,
      comment: isWaiter ? comment : '',
      flyImageFallback: restaurantLogoUrl ?? undefined,
      budgetExceeded,
      addSharedLine,
      isBuffetDineIn,
      mode: activeMode,
    });
    if (budgetExceeded) return;
    // SC-850, retour QA : ne pas dispatcher openDrawer() ne suffisait pas a
    // garantir le resultat sur les parcours consumer. Un etat UI residuel
    // pouvait redevenir visible au moment ou la fiche se fermait. La regle est
    // donc une postcondition explicite : apres un ajout reussi, le tiroir est
    // ferme sur les quatre surfaces (emporter, livraison, QR et serveur).
    // L'accuse de reception reste le vol vers le panier et son compteur ; le
    // tiroir ne s'ouvre que par son propre bouton.
    dispatch(closeDrawer());
    onClose();
  };

  const renderCommentField = () => (
    <div className="mt-4">
      <label htmlFor="product-comment" className="block font-display text-base mb-1">
        {t('product.commentLabel')}
      </label>
      <textarea
        id="product-comment"
        value={comment}
        onChange={(event) => setComment(event.target.value)}
        maxLength={255}
        rows={2}
        placeholder={t('product.commentPlaceholder')}
        className="w-full resize-none border-2 border-ink bg-paper px-3 py-2 font-mono text-sm text-ink placeholder:text-ink/45 focus:outline-none focus:ring-2 focus:ring-mint-deep"
      />
      <p className="m-0 text-right font-mono text-xs text-ink/50">{comment.length} / 255</p>
    </div>
  );

  const renderGroupOptions = (g: OptionGroup) => {
    // Des qu'une option du groupe porte une photo, toutes reservent la place :
    // sinon les noms du groupe partent de trois abscisses differentes selon que
    // l'option est illustree — flagrant sur l'ecran unique du serveur, ou les
    // groupes sont empiles.
    const groupHasPhoto = g.options.some((o) => !!o.photo);
    const groupMax = maxAllowed(g);
    const groupCount = selectedCount(g.id);
    const atMax = g.multiple && groupCount >= groupMax;
    const showMaxHint = g.multiple && Number.isFinite(groupMax);
    return (
      <div key={g.id} className="mb-4">
        <h3 className="font-display text-base mb-2">
          {nameOf(g)} {isEffectivelyRequired(g) && <span className="text-hot-pink">*</span>}
          {g.multiple && (
            <span className="text-ink/60 text-xs ms-2 font-mono">
              {showMaxHint ? t('product.maxHint', { n: groupCount, max: groupMax }) : t('product.several')}
            </span>
          )}
        </h3>
        <ul className="space-y-2 list-none p-0 m-0">
          {g.options.map((opt) => {
            const checked = isOptionSelected(g.id, opt.id);
            if (g.multiple) {
              const currentQty = optionQty(g.id, opt.id);
              const minQ = optionMinQty(opt);
              const maxQ = optionMaxQty(opt);
              const groupRemaining = maxAllowed(g) - selectedCount(g.id);
              // Entry costs minQ slots of the group budget; afterwards +1 costs
              // one slot and must respect the per-option cap.
              const plusDisabled =
                currentQty === 0
                  ? groupRemaining < minQ || minQ > maxQ
                  : groupRemaining < 1 || currentQty >= maxQ;
              return (
                <li key={opt.id}>
                  <div
                    className={`flex items-center gap-3 px-3 py-2 border-2 font-mono text-sm transition-colors ${
                      checked
                        ? 'border-ink bg-mint/30'
                        : 'border-ink/30 bg-paper'
                    }`}
                  >
                    {groupHasPhoto &&
                      (opt.photo ? (
                        <img
                          src={opt.photo}
                          alt={nameOf(opt)}
                          className="ms-2 h-10 w-10 flex-none rounded object-cover"
                        />
                      ) : (
                        <span aria-hidden="true" className="ms-2 h-10 w-10 flex-none" />
                      ))}
                    <span className="flex-1">{nameOf(opt)}</span>
                    {!buffetIncluded && opt.priceDelta > 0 && (
                      <Price amountCents={opt.priceDelta} className="text-xs" />
                    )}
                    <div
                      className="flex items-center gap-1 font-mono text-sm"
                      data-testid={`opt-stepper-${opt.id}`}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setOptionQty(g, opt.id, currentQty - 1 < minQ ? 0 : currentQty - 1);
                        }}
                        disabled={currentQty === 0}
                        aria-label={t('product.removeOption', { label: nameOf(opt) })}
                        className="w-9 h-9 md:w-7 md:h-7 border-2 border-ink bg-paper flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                      >
                        −
                      </button>
                      <span className="w-7 md:w-6 text-center font-bold">{currentQty}</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setOptionQty(g, opt.id, currentQty === 0 ? minQ : currentQty + 1);
                        }}
                        disabled={plusDisabled}
                        aria-label={t('product.addOption', { label: nameOf(opt) })}
                        className="w-9 h-9 md:w-7 md:h-7 border-2 border-ink bg-mint text-[var(--on-primary)] flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </li>
              );
            }
            const disabled = atMax && !checked;
            return (
              <li key={opt.id}>
                <label
                  className={`flex items-center gap-3 px-3 py-2 border-2 font-mono text-sm transition-colors ${
                    checked
                      ? 'border-ink bg-mint/30 cursor-pointer'
                      : disabled
                        ? 'border-ink/20 bg-paper opacity-50 cursor-not-allowed'
                        : 'border-ink/30 bg-paper hover:border-ink hover:bg-cream cursor-pointer'
                  }`}
                >
                  <input
                    type={!g.multiple && g.required ? 'radio' : 'checkbox'}
                    name={g.id}
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggleSingleChoice(g, opt.id)}
                    className="cursor-pointer accent-mint-deep disabled:cursor-not-allowed"
                  />
                  {groupHasPhoto &&
                    (opt.photo ? (
                      <img
                        src={opt.photo}
                        alt={nameOf(opt)}
                        className="ml-2 h-10 w-10 flex-none rounded object-cover"
                      />
                    ) : (
                      <span aria-hidden="true" className="ml-2 h-10 w-10 flex-none" />
                    ))}
                  <span className="flex-1">{nameOf(opt)}</span>
                  {!buffetIncluded && opt.priceDelta > 0 && (
                    <Price amountCents={opt.priceDelta} className="text-xs" />
                  )}
                </label>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  // Ecran unique — le plat sans option (tout le monde), et le carnet du serveur
  // meme quand le plat a des options. L'assistant pas-a-pas coute un « Suivant »
  // par groupe : il guide le client qui decouvre la carte, il freine le serveur
  // qui la connait et veut voir la garniture, la cuisson et la quantite d'un
  // seul coup d'oeil.
  if (itemGroups.length === 0 || isWaiter) {
    return (
      <Modal open={open} onClose={onClose} ariaLabel={itemName} origin={origin} instant={isWaiter}>
        <div className="flex flex-col max-h-[min(720px,calc(100dvh-24px))] md:max-h-[min(720px,calc(100dvh-48px))] w-full">
          {/* Carnet du serveur : pas de photo d'en-tete. Elle prend 236 px, soit
              28 % de l'ecran d'un telephone, avant le premier choix — sur un
              plat a deux groupes d'options, c'est ce qui empeche de tout voir
              d'un coup. Le serveur a deja vu la ligne dans la carte ; ce qu'il
              vient chercher ici, ce sont les options. */}
          {isWaiter ? (
            <div className="flex shrink-0 items-center gap-2 border-b-2 border-ink bg-cream px-3 py-2">
              <h2 className="m-0 min-w-0 flex-1 font-display text-lg leading-tight text-ink">
                <DishCodeTag name={itemName} code={item.code} />
                {itemName}
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 w-10 h-10 bg-paper border-2 border-ink font-display text-2xl leading-none cursor-pointer"
                aria-label={t('common.close')}
              >
                ×
              </button>
            </div>
          ) : (
            <div className="relative bg-ash shrink-0 border-b-2 border-ink overflow-hidden">
              <ProductImage
                photo={item.photo}
                restaurantLogoUrl={restaurantLogoUrl}
                restaurantName={restaurantName}
                themeColor={themeColor}
                alt={itemName}
                variant="modal"
                loading="eager"
              />
              {item.hot && <SteamLayer intensity="strong" />}
              <button
                type="button"
                onClick={onClose}
                className="absolute top-3 end-3 w-10 h-10 bg-paper border-2 border-ink font-display text-2xl leading-none cursor-pointer transition-all hover:bg-hot-pink hover:text-paper hover:-translate-x-[1px] hover:-translate-y-[1px]"
                aria-label={t('common.close')}
              >
                ×
              </button>
            </div>
          )}
          <div className={isWaiter ? 'flex-1 min-h-0 overflow-y-auto px-3 py-2' : 'flex-1 min-h-0 overflow-y-auto p-4 md:p-6'}>
            {!isWaiter && (
              <h2 className="font-display text-2xl text-ink mb-2"><DishCodeTag name={itemName} code={item.code} size="lg" />{itemName}</h2>
            )}
            {itemDescription ? (
              <p className={`font-mono text-sm text-ink/80 whitespace-pre-line ${isWaiter ? 'mb-2' : 'mb-4'}`}>{itemDescription}</p>
            ) : null}
            {!isWaiter && <AllergenSection codes={allergens?.contains} traces={allergens?.traces} restaurantDeclares={allergensDeclared} />}
            {itemGroups.map((g) => renderGroupOptions(g))}
            {isWaiter && renderCommentField()}
          </div>
          {orderingEnabled ? (
            <div className="shrink-0 border-t-2 border-ink p-3 md:p-4 bg-cream">
              {/* Au pied et pas dans le corps : sur un plat a beaucoup d'options
                  la liste defile, et le rappel doit rester sous les yeux, a
                  cote du bouton qu'il explique. En `text-ink` et pas en
                  `text-hot-pink` : cette derniere est l'accent du restaurant,
                  et sur un theme clair (jaune par exemple) le rappel devenait
                  illisible sur le creme du pied. */}
              {missingRequiredLabels.length > 0 && (
                <p data-testid="required-missing" className="m-0 mb-2 font-mono text-xs font-bold text-ink">
                  {t('product.requiredMissing', { labels: missingRequiredLabels.join(', ') })}
                </p>
              )}
              <div className="flex items-center gap-3">
                <div className="md:hidden">
                  <QtyStepper qty={qty} onChange={setQty} size="sm" />
                </div>
                <div className="hidden md:block">
                  <QtyStepper qty={qty} onChange={setQty} />
                </div>
                <button
                  type="button"
                  onClick={onAdd}
                  disabled={!allRequiredFilled || addDisabledByBudget}
                  className="flex-1 min-w-0 bg-mint text-[var(--on-primary)] border-2 border-ink px-3 py-3 md:px-5 md:py-4 font-display flex items-center justify-between gap-2 cursor-pointer transition-all hover:-translate-x-[1px] hover:-translate-y-[1px] disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ boxShadow: 'var(--shadow-stamp)' }}
                  onMouseEnter={(e) => {
                    if (!e.currentTarget.disabled)
                      e.currentTarget.style.boxShadow = 'var(--shadow-stamp-lg)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'var(--shadow-stamp)';
                  }}
                >
                  <span className="text-sm md:text-[16px] min-w-0 truncate">{t('product.addToCart')}</span>
                  <span className="font-mono font-bold text-sm md:text-[16px] whitespace-nowrap shrink-0">
                    <Price amountCents={unitPrice * qty} className="text-[13px] md:text-[15px]" />
                  </span>
                </button>
              </div>
            </div>
          ) : (
            <div className="shrink-0 border-t-2 border-ink p-4 bg-cream font-mono text-sm text-ink/70 text-center">
              {t('restaurant.noOnlineOrders')}
            </div>
          )}
        </div>
      </Modal>
    );
  }

  // Stepper layout
  const currentGroup = itemGroups[step];
  const isLastStep = step === itemGroups.length - 1;
  const currentCount = selectedCount(currentGroup.id);
  const currentMin = minRequired(currentGroup);
  const currentFilled = currentCount >= currentMin;

  // Footer primary button logic
  let primaryText: React.ReactNode;
  let primaryDisabled: boolean;

  if (!isLastStep) {
    // A group that requires a selection (required, or min >= 1 — see SC-107)
    // blocks "Suivant" until its minimum is met. A truly optional group has
    // min 0, so it is already "filled" and advances freely.
    primaryText = t('product.next');
    primaryDisabled = !currentFilled;
  } else {
    const addDisabled = !allRequiredFilled || addDisabledByBudget;
    primaryText = (
      <>
        <span className="text-sm md:text-[16px] min-w-0 truncate">{t('product.addToCart')}</span>
        <span className="font-mono font-bold text-sm md:text-[16px] whitespace-nowrap shrink-0">
          <Price amountCents={unitPrice * qty} className="text-[13px] md:text-[15px]" />
        </span>
      </>
    );
    primaryDisabled = addDisabled;
  }

  return (
    <Modal open={open} onClose={onClose} ariaLabel={itemName} origin={origin}>
      <div className="flex flex-col max-h-[min(720px,calc(100dvh-24px))] md:max-h-[min(720px,calc(100dvh-48px))] w-full">
        {/* Header: image + close */}
        <div className="relative bg-ash shrink-0 border-b-2 border-ink overflow-hidden">
          <ProductImage
            photo={item.photo}
            restaurantLogoUrl={restaurantLogoUrl}
            restaurantName={restaurantName}
            themeColor={themeColor}
            alt={itemName}
            variant="modal"
            loading="eager"
          />
          {item.hot && <SteamLayer intensity="strong" />}
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 end-3 w-10 h-10 bg-paper border-2 border-ink font-display text-2xl leading-none cursor-pointer transition-all hover:bg-hot-pink hover:text-paper hover:-translate-x-[1px] hover:-translate-y-[1px]"
            aria-label={t('common.close')}
          >
            ×
          </button>
        </div>

        {/* Body: name, description, breadcrumb, current group */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6">
          <h2 className="font-display text-2xl text-ink mb-2"><DishCodeTag name={itemName} code={item.code} size="lg" />{itemName}</h2>
          <p className="font-mono text-sm text-ink/80 mb-4 whitespace-pre-line">{itemDescription}</p>
          <AllergenSection codes={allergens?.contains} traces={allergens?.traces} restaurantDeclares={allergensDeclared} />

          {/* Breadcrumb strip */}
          <div
            className="flex gap-2 mb-4 overflow-x-auto whitespace-nowrap"
            role="navigation"
            aria-label={t('product.steps')}
          >
            {itemGroups.map((g, i) => {
              const isCurrent = i === step;
              return (
                <button
                  key={g.id}
                  type="button"
                  data-testid={`breadcrumb-${i}`}
                  onClick={() => setStep(i)}
                  aria-current={isCurrent ? 'step' : undefined}
                  className={`px-3 py-1 border-2 font-mono text-xs shrink-0 transition-colors ${
                    isCurrent
                      ? 'border-ink bg-mint text-[var(--on-primary)] font-bold cursor-default'
                      : 'border-ink bg-paper cursor-pointer hover:bg-cream hover:-translate-y-px'
                  }`}
                >
                  {nameOf(g)}
                </button>
              );
            })}
          </div>

          {/* Current group options */}
          {renderGroupOptions(currentGroup)}
          {isWaiter && isLastStep && renderCommentField()}
        </div>

        {/* Footer */}
        {orderingEnabled ? (
          <div className="shrink-0 border-t-2 border-ink p-2 md:p-4 flex items-center gap-2 md:gap-3 bg-cream">
            {/* Left: Précédent (icon-only on mobile, full text on md+) */}
            <button
              type="button"
              onClick={() => setStep((s) => s - 1)}
              disabled={step === 0}
              aria-label={t('product.previous')}
              className="shrink-0 px-3 py-3 md:px-4 md:py-4 border-2 border-ink font-display bg-paper text-sm cursor-pointer transition-all hover:-translate-x-[1px] hover:-translate-y-[1px] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:translate-x-0 disabled:hover:translate-y-0"
              style={{ boxShadow: 'var(--shadow-stamp)' }}
            >
              <span className="md:hidden">←</span>
              <span className="hidden md:inline">{t('product.previousFull')}</span>
            </button>

            {/* Middle: QtyStepper only on last step (smaller on mobile) */}
            {isLastStep && (
              <div className="md:hidden">
                <QtyStepper qty={qty} onChange={setQty} size="sm" />
              </div>
            )}
            {isLastStep && (
              <div className="hidden md:block">
                <QtyStepper qty={qty} onChange={setQty} />
              </div>
            )}

            {/* Right: primary action */}
            {isLastStep ? (
              <button
                type="button"
                onClick={onAdd}
                disabled={primaryDisabled}
                className="flex-1 min-w-0 bg-mint text-[var(--on-primary)] border-2 border-ink px-3 py-3 md:px-5 md:py-4 font-display flex items-center justify-between gap-2 cursor-pointer transition-all hover:-translate-x-[1px] hover:-translate-y-[1px] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-x-0 disabled:hover:translate-y-0"
                style={{ boxShadow: 'var(--shadow-stamp)' }}
                onMouseEnter={(e) => {
                  if (!e.currentTarget.disabled)
                    e.currentTarget.style.boxShadow = 'var(--shadow-stamp-lg)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'var(--shadow-stamp)';
                }}
              >
                {primaryText}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setStep((s) => s + 1)}
                disabled={primaryDisabled}
                className="flex-1 min-w-0 bg-mint text-[var(--on-primary)] border-2 border-ink px-3 py-3 md:px-5 md:py-4 font-display text-sm cursor-pointer transition-all hover:-translate-x-[1px] hover:-translate-y-[1px] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-x-0 disabled:hover:translate-y-0"
                style={{ boxShadow: 'var(--shadow-stamp)' }}
              >
                {primaryText}
              </button>
            )}
          </div>
        ) : (
          <div className="shrink-0 border-t-2 border-ink p-4 bg-cream font-mono text-sm text-ink/70 text-center">
            {t('restaurant.noOnlineOrders')}
          </div>
        )}
      </div>
    </Modal>
  );
}
